import { resolveRouting, resolveLocalCommittee, resolveGeneralCommittee } from './routing';
import { getClaimRequirements } from './claimRequirements';
import { filerIsClaimant } from './memberProfile';
import { cleanEmail } from './contactCheck';
import { lastDayOf } from './timeLimit';   // forensic audit 2026-10-16: the expiry date in UTC, never via local midnight

// ONE BUILDER. EVERY CLAIM DOCUMENT IS CONSTRUCTED HERE AND NOWHERE ELSE.
//
// Before 2026-08-23 six sites built a claim payload by hand — NewClaim's non-agreement route, its
// save-draft, its submit, an inline major-dispute auto-save on the Subject-of-Claim select, and
// QuickFile's save-draft and submit. Eighty distinct fields appeared across them and no two wrote
// the same set, so the SHAPE OF A CLAIM DEPENDED ON WHICH BUTTON THE MEMBER PRESSED. The census and
// the nine defects it turned up are in data\YCP_CLAIM_FIELD_CENSUS_2026-08-23.md; the field set
// below is the one Bob Keeley ruled on, 2026-08-23, at SS6.7 of that file.
//
// This module imports NOTHING from firebase on purpose. `serverTimestamp` is passed in by the
// caller, which is what lets the whole payload be exercised by a plain node script instead of by
// clicking through a deployed app (PD-15: a step is verified by running something).

// The claimant contact block. It is a SNAPSHOT AT FILING, not a live copy of users/{uid} — Bob's
// ruling of 2026-08-23. A grievance is a record of a moment, and the address that mattered is the
// address at filing, so a claim that renders differently a year later because the member moved is a
// worse defect than the duplication. That duplication is a deliberate SS XI cost, named here so no
// later reader mistakes it for an accident and "fixes" it.
//
// Listed explicitly, and always written, because of defect D-3: these six lived only in NewClaim's
// profile-prefill effect, inside `if (snap.exists())`, so a member with no users/{uid} document
// filed a claim with no contact block at all. A snapshot that is sometimes not taken is not a
// snapshot.
const CONTACT_FIELDS = [
  'claimantName', 'claimantEmail', 'claimantPhone',
  'claimantStreet', 'claimantCity', 'claimantState', 'claimantZip',
];

// Identity and service facts about the claimant. Absent is legitimate — QuickFile does not capture
// them — but WHICH BUTTON WAS PRESSED must not be what decides it.
const CLAIMANT_FIELDS = [
  'rosterNumber', 'seniorityDate', 'employeeNumber',
  'claimantJobSymbol', 'claimantTourOfDuty', 'hosPosition',
  'shiftLength', 'shiftEnd',
];

const WHERE_WHEN_FIELDS = [
  'craft', 'railroad', 'workLocation', 'eventLocation',
  'incidentDate', 'incidentTime24',
  // RULING 179 (2026-09-17), ISS-21. The CONTESTED POSITION, which is not the same thing as the
  // craft. The craft vocabulary is two values - Yardmaster, Mechanical Supervisor - and CSX
  // Addendum H carries a CHIEF YARDMASTER rule with its own calculation. claimValue.js has always
  // had the position-class axis and until today NOTHING WROTE TO IT: no claim carried the field, so
  // every valuation fell through to the craft and a Chief Yardmaster claim was silently valued at
  // the Yardmaster rate. This is the field that feeds it. Optional - a claim without it is still
  // valued, and valueClaim() now says out loud which class the figure rests on.
  'contestedPosition',
];

const SUBJECT_FIELDS = [
  'subjectType', 'subjectCraftType', 'subjectSeniorityDate',
  'violatorName', 'violatorRosterNumber', 'violatorAssignment', 'violatorRestDays',
  'violatorIsNonAgreement',
];

const BASIS_FIELDS = [
  'description', 'personsInvolved', 'witnesses', 'remedySought',
];

const MONEY_FIELDS = [
  'hourlyRateST', 'hourlyRateOT', 'totalHoursClaimed',
  'totalAmountST', 'totalAmountOT', 'isOvertimeClaim',
];

// PRESENTMENT AND NARRATIVE - CAPTURED BY components\\ClaimBasisBoxes.js, NOT BY EITHER FORM'S
// INITIAL STATE, WHICH IS EXACTLY HOW THIS BUILDER LOST THEM.
//
// ADDED 2026-08-23, correcting a regression THIS FILE introduced the same morning. The field census
// was derived from the two components' initial state and from the old payload literals. These five
// are in neither: ClaimBasisBoxes renders one question per requirement the agreement names and
// writes the answer straight into the bag with onSet(). Under the old `...form` spread that was
// invisible and harmless - everything in the bag was saved. An ALLOW-LIST turns it into data loss,
// and the census said so itself at D-2 ("the written key set at runtime is not bounded by this
// census") before this file went and bounded it anyway.
//
// What was being thrown away: `presentedTo` is an ELIGIBILITY requirement under Rule 29(a) and CSX
// Article 20(a) - who the claim was presented to is part of what makes it a valid claim at all.
const PRESENTMENT_FIELDS = [
  'presentedTo', 'proofOfDelivery', 'proofOfDeliveryMethod',
  'detailsOfWork', 'reasonsSupporting',
];

// EVERY KEY THE FORM LEGITIMATELY OWNS, and the ONLY keys a stored draft may put back into form
// state. Exported for `claimDrafts.draftIntoForm()`, which is what NewClaim now merges through.
//
// WHY THIS EXISTS. Defect D-2: resuming a draft did `setForm(prev => ({ ...prev, ...data }))`, so
// EVERY key the stored document carried was re-admitted to the form bag - including keys this
// builder deliberately drops, and including the four routing fields it resolves fresh precisely so
// a caller's bag can never reach them. The builder's allow-list closed the WRITE path; the form was
// still an open READ path back into it. A stale `scopeKey` could not be written directly any more,
// but it could ride back in on a resumed draft and change what `resolveRouting` was handed.
//
// The list is BUILT from the same arrays the builder writes, not typed out again, so the two cannot
// drift. The extras below are the keys the builder reads off the form that are not in those arrays.
//
// `generalCommittee` is DELIBERATELY ABSENT even though the builder reads it: it is a DROPPED field,
// so no draft written by this builder carries one, and a LEGACY draft that does carries a stale one.
// The intake screens derive it from craft + railroad on load. Letting it back in would re-open D-2
// through the one field that decides committee resolution.
const FORM_OWNED_EXTRAS = [
  'applicableAgreement', 'priority',
  'notes', 'documents', 'boardAwards', 'exhibits',
];

// RULING 261 / 258 (2026-09-29). The guided Box 1: the kind of claim, the answers to its short questions, and
// the Statement of Claim as the claimant (or the officer filing for her) approved it. ADDITIVE ONLY: `pick`
// writes a key only when the form bag has it, and the bag has these only while BOX1_GUIDED is on - so with the
// switch off no claim is written with them, and a claim that has them keeps them untouched (exact reversal).
const GUIDED_FIELDS = ['claimKind', 'box1Answers', 'statementOfClaim'];

// SECTION 510 (2026-09-29): Quick File's Box 3 answers, as [{ id, q, answer }] (src\\data\\box3Answers.js).
// SECTION 536 (2026-09-30): what work a junior-ahead or unnumbered-article claim is about, as the claimant answered it.
const DETAIL_FIELDS = ['box3Answers', 'workKind', 'workKindNote', 'descriptionAsWritten'];   // section 540: his words as typed, before the automatic spelling pass

export const FORM_FIELDS = [].concat(
  CONTACT_FIELDS, CLAIMANT_FIELDS, WHERE_WHEN_FIELDS, SUBJECT_FIELDS,
  BASIS_FIELDS, MONEY_FIELDS, PRESENTMENT_FIELDS, FORM_OWNED_EXTRAS, GUIDED_FIELDS, DETAIL_FIELDS,
);

// DROPPED BY THE RULING. Each is a copy or a rendering of a field that is kept, and the builder
// must never write one. Kept as a named list so the intent survives: this is not an oversight.
//   generalCommittee   duplicate of `committee`, itself a projection of `scopeKey`
//   rulesViolated      a join('; ') of matchedRules
//   violationType      replaced by the stored archetype, violationTypes
//   violationTypeOther meaningful only while violationType is stored
//   yardOfIncident     workLocation and eventLocation already carry it
//   incidentTime12     a display format of incidentTime24
//   filedAt            duplicate of createdAt on submit, and written on DRAFTS where nothing was filed
//   draftOwnerId       duplicate of createdBy — and not the field the draft-adoption query reads
//   draftOwnerEmail    duplicate of filedBy
export const DROPPED_FIELDS = [
  'generalCommittee', 'rulesViolated', 'violationType', 'violationTypeOther',
  'yardOfIncident', 'incidentTime12', 'filedAt', 'draftOwnerId', 'draftOwnerEmail',
  // RULING 251.1 (2026-09-29, Bob: "eliminating this part of the claim altogether"): internal comments are
  // gone from the claim. Read-only count before removal: 12 claims, 0 comments
  // (data\YCP_INTERNAL_COMMENTS_COUNT_2026-09-29.txt). Claim saves use updateDoc, so nothing stored is erased.
  'internalComments',
];

// NO DEFAULT WINDOW. There used to be a hardcoded 30 days here, carried over from QuickFile, and
// it was wrong twice over: wrong ANCHOR (the day of filing instead of the date of occurrence) and
// wrong LENGTH (30 instead of the 60 the agreement actually gives). It only wrote one of six paths
// before today, so the D-5 fix made a wrong number universal, which is worse than the gap it
// closed. Caught on 2026-08-23 by reading the requirements panel on a live claim: the screen said
// the deadline was 2026-09-25, sixty days from a 2026-07-27 occurrence per Rule 29(a)/(b), while
// this file was about to store 2026-09-22. Same claim, two deadlines, and the stored one earlier.
//
// The window is a fact about the AGREEMENT and it already lives in src\\data\\claimRequirements.js,
// where components\\EligibilityPayablePanel.js reads it to draw the deadline the member is shown.
// It is read from the same place here so the two cannot disagree (SS XI).

function pick(src, keys, out) {
  for (const k of keys) if (src[k] !== undefined) out[k] = src[k];
  return out;
}

// Always a number, never a literal 0 dropped in because the path happened to be a draft.
// Defect D-4: four of the six old sites hardcoded 0, INCLUDING QuickFile's submit, so every claim
// filed through QuickFile was valued at zero.
export function computeAmountClaimed(f) {
  const raw = f.isOvertimeClaim ? f.totalAmountOT : f.totalAmountST;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

// The filing deadline is the one thing a grievance cannot afford to lose, and before today exactly
// one of the six sites wrote it (defect D-5). data\DIGITAL_EXPIRY_LEDGER.csv exists to track it.
export function computeExpiresAt(f) {
  const req = getClaimRequirements(f && f.applicableAgreement);
  const days = req && req.filingWindowDays;
  const on = f && f.incidentDate;
  if (!days || !on) return null;              // unknowable -> write nothing (PD-4, no fabrication)
  // Forensic audit 2026-10-16: this built LOCAL midnight and then read the date back in UTC (toISOString) - in any zone
  // east of UTC that is the previous day, so the deadline came out ONE DAY EARLY. timeLimit.lastDayOf counts in UTC on
  // the date alone (ruling 314: the occurrence is day one - the last day is N - 1 later). A filing deadline is a DATE.
  return lastDayOf(String(on).slice(0, 10), days);
}

/**
 * Build the one canonical claim document.
 *
 * @param {object}   o
 * @param {object}   o.form           the caller's field bag (NewClaim `form`, or QuickFile's `claim`)
 * @param {string}   o.uid            auth uid — becomes createdBy, the ownership authority
 * @param {string}   o.email          auth email — becomes filedBy, a denormalised display copy
 * @param {boolean}  o.profileConfirmed  the REAL value. Defect D-6: QuickFile hardcoded false.
 * @param {Array}    o.matchedRules
 * @param {Array}    o.violationTypes archetype ids. Ruling 3 of 2026-08-22, reaching code here.
 * @param {string}   o.approvedNarrative
 * @param {string}   o.mode           'draft' | 'submit'
 * @param {string}  [o.variant]       undefined | 'nonAgreement' | 'majorDispute'
 * @param {string}  [o.claimNumber]   required for 'submit'; becomes id and title
 * @param {*}        o.serverTimestamp   the VALUE from firebase's serverTimestamp(), injected
 * @param {boolean} [o.isNew]         true when this is an addDoc; adds createdAt
 */
export function buildClaimPayload(o) {
  const f = o.form || {};
  const mode = o.mode === 'submit' ? 'submit' : 'draft';
  const ts = o.serverTimestamp;

  // ROUTING IS RESOLVED FRESH AND IT WINS. Defect D-1: handleSaveDraft spread resolveRouting()
  // FIRST and handleSubmit spread it LAST, so in the same file saving a draft could pin a stale
  // scopeKey that submitting would recompute. Resuming a draft merges the whole stored document
  // back into form state (D-2), which is what put a stale scopeKey within reach in the first place.
  // Here the caller's bag cannot reach these four fields at all.
  const routing = resolveRouting(f.craft, f.railroad, f.workLocation, f.generalCommittee);
  const committee = resolveGeneralCommittee(routing.scopeKey) || (f.generalCommittee || '');
  const localCommittee = resolveLocalCommittee(f.craft, f.railroad, f.workLocation);

  const out = {};

  // --- claimant: contact block ALWAYS present, empty string rather than absent
  for (const k of CONTACT_FIELDS) out[k] = f[k] === undefined || f[k] === null ? '' : f[k];
  // Bob 2026-10-14: a trailing period on the member's e-mail silently defeated the link to his account (contactCheck.js).
  out.claimantEmail = cleanEmail(out.claimantEmail);
  pick(f, CLAIMANT_FIELDS, out);
  pick(f, WHERE_WHEN_FIELDS, out);
  pick(f, SUBJECT_FIELDS, out);
  pick(f, BASIS_FIELDS, out);
  pick(f, MONEY_FIELDS, out);
  pick(f, PRESENTMENT_FIELDS, out);
  pick(f, DETAIL_FIELDS, out);   // section 510: Box 3 answers
  pick(f, GUIDED_FIELDS, out);   // ruling 261: present only when the guided Box 1 put them in the bag

  // --- basis
  out.matchedRules = Array.isArray(o.matchedRules) ? o.matchedRules : [];
  // RULING 3 of 2026-08-22, reaching code here: the archetype is STORED on the claim as the join
  // key to a verified quote and its line, so storing it stores the provenance of the whole claim
  // basis. It is DERIVED from matchedRules rather than passed in, because the id is already on the
  // rule row - Plan 8 has the model return a violation id and the Cloud Function fill the quote in
  // from the map. Deriving it here means the archetype and the rule it came from cannot drift.
  //
  // This stores nothing that matchedRules does not already store. RULING 4 - infer, then ask the
  // member to confirm before anything is kept - is a change to the intake screens and is NOT done
  // by this builder; it remains open.
  out.violationTypes = Array.isArray(o.violationTypes) && o.violationTypes.length
    ? o.violationTypes
    : deriveViolationTypes(out.matchedRules);
  out.approvedNarrative = o.approvedNarrative || '';
  out.applicableAgreement = f.applicableAgreement || '';

  // --- routing, authoritative
  out.scopeKey = routing.scopeKey;
  out.routingStatus = routing.routingStatus;
  out.committee = committee;
  out.localCommittee = localCommittee;

  // --- ownership
  out.createdBy = o.uid || '';
  // PLAN R-4 / finding C-2 (2026-09-20): WHO THE CLAIM IS ABOUT, written at filing. The claims read rule
  // admitted only the creator, so a claim an officer filed for a member was unreadable BY THAT MEMBER -
  // the arm ruling 176 gave the discipline path and claims never had. `claimantUid` is set only when the
  // filer IS the claimant (the e-mail on the form is the signed-in account's); an officer filing for a
  // member leaves it empty and the member is reached by `claimantEmail`, exactly as ruling 176 reads it.
  // Never inferred from a name: two members share a name, and a wrong uid would open one man's claim to another.
  // R-5 (2026-09-20): the test R-4 wrote inline here now lives once in memberProfile.js and is
  // read by this builder and by both intake screens. One source, three readers (directives XI).
  out.claimantUid = (o.claimantUid && String(o.claimantUid))
    || (filerIsClaimant({ filerEmail: o.email, claimantEmail: cleanEmail(o.form && o.form.claimantEmail) }) ? (o.uid || '') : '');
  out.filedBy = o.email || '';
  out.profileConfirmed = !!o.profileConfirmed;
  // BOB, 2026-10-25: the claimant answers each section of his own claim - "Correct - no change" or an
  // edit - and the answer is recorded with the moment he gave it (srcdataclaimAck.js). A year later
  // the record shows he confirmed his own identity data at filing, which is worth more than the data
  // itself when a carrier argues the claim names the wrong man. Written only for the sections he
  // actually answered; an absent row is not a denial.
  if (o.sectionAcks && typeof o.sectionAcks === "object" && Object.keys(o.sectionAcks).length) {
    out.sectionAcks = o.sectionAcks;
  }

  // --- money
  out.amountClaimed = computeAmountClaimed(f);
  out.priority = f.priority || 'standard';

  // --- collections
  out.notes = Array.isArray(f.notes) ? f.notes : [];
  out.documents = Array.isArray(f.documents) ? f.documents : [];
  out.boardAwards = Array.isArray(f.boardAwards) ? f.boardAwards : [];
  out.exhibits = Array.isArray(f.exhibits) ? f.exhibits : [];

  // --- lifecycle
  out.isTestData = false;
  // Absent when the agreement is uncatalogued or no date of occurrence has been given yet. An
  // ABSENT deadline is honest; an invented one is a number a General Chairperson might rely on.
  const expires = computeExpiresAt(f);
  if (expires) out.expiresAt = expires;
  out.updatedAt = ts;
  if (o.isNew) out.createdAt = ts;

  if (mode === 'submit') {
    // ONE WRITE, NOT TWO. Defect D-9: all three old submit paths did addDoc with status 'draft'
    // and then immediately updateDoc to 'submitted'. A failure between the two left a filed claim
    // recorded as a draft, and nothing ever reconciled that state.
    out.status = 'submitted';
    out.submittedAt = ts;
    out.id = o.claimNumber || '';
    out.title = o.claimNumber || '';
  } else {
    out.status = 'draft';
    out.title = draftTitle(f);
  }

  if (o.variant === 'nonAgreement') {
    out.claimSubtype = 'non-agreement-employee';
    out.documentType = 'letter-of-non-acquiescence';
    out.routedTo = ['local_chair', 'general_chair'];
    out.routedAt = ts;
  }

  if (o.variant === 'majorDispute') {
    out.subjectType = 'management';
    out.majorDisputeFlag = true;
    // No notification stamp. Saving this draft notifies no one, so the record
    // does not say anyone was notified (audit 2026-10-16, finding 1).
  } else if (f.majorDisputeFlag !== undefined) {
    out.majorDisputeFlag = f.majorDisputeFlag;
  }

  return out;
}

// Unique, order-preserving. A rule with no violationId contributes nothing rather than an empty
// string: the id is a join key into data\\YCP_VIOLATION_RULE_MAP.csv and a blank one joins to
// nothing.
function deriveViolationTypes(rules) {
  const out = [];
  for (const r of rules || []) {
    const id = r && typeof r.violationId === 'string' ? r.violationId.trim() : '';
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

// A draft has no claim number — those are a scarce ordered resource and are taken at submit. The
// title is therefore derived, by ONE rule instead of the four the old sites used between them.
function draftTitle(f) {
  const d = (f.description || '').trim();
  if (d) return d.slice(0, 60);
  return 'Untitled draft';
}

// Legacy read helper. Documents written before 2026-08-23 carry the dropped fields and documents
// written after do not, so every reader needs both for as long as pre-cutover claims exist. Kept
// here, beside the drop list, so the two can never drift apart.
export function claimCommittee(c) {
  return (c && (c.committee || c.generalCommittee)) || '';
}
export function claimFiledAtSeconds(c) {
  if (!c) return 0;
  return (c.createdAt && c.createdAt.seconds) || (c.filedAt && c.filedAt.seconds) || 0;
}
export function claimBasisLabel(c) {
  if (!c) return '';
  if (Array.isArray(c.matchedRules) && c.matchedRules.length) {
    return c.matchedRules.map(r => r && r.rule).filter(Boolean).join('; ');
  }
  return c.rulesViolated || c.violationType || c.claimType || '';
}
export function claimYard(c) {
  if (!c) return '';
  if (c.yardOfIncident) return c.yardOfIncident;
  const w = c.workLocation || '';
  const e = c.eventLocation || '';
  return e && e !== w ? `${w} — ${e}` : w;
}
